import React, { useState, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search, MapPin, Shield, Wifi, GraduationCap, Train, Bus,
  AlertTriangle, TrendingUp, TrendingDown, Minus, Home,
  Building2, TreePine, Landmark, Droplets, ShieldAlert
} from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { computeAreaSignal, describeAreaSignal } from '../components/ui/AreaBadge';
import SalesHistoryList from '../components/properties/SalesHistoryList';
import { formatReligiousCharacter, formatSchoolPhase } from '../utils/schoolLabels';

const BANNER_STYLE = {
  green: { ring: 'border-emerald-500/40 bg-emerald-500/10', dot: 'bg-emerald-400', text: 'text-emerald-200', label: 'Strong area' },
  amber: { ring: 'border-amber-500/40 bg-amber-500/10',     dot: 'bg-amber-400',   text: 'text-amber-200',   label: 'Mixed area' },
  red:   { ring: 'border-red-500/40 bg-red-500/10',         dot: 'bg-red-400',     text: 'text-red-200',     label: 'Tough area' },
};

function AreaSummaryBanner({ report }) {
  const signal = computeAreaSignal({
    imd_rank: report.imd_rank,
    crime_rate_band: report.crime?.rate_band,
  });
  if (!signal) return null;
  const style = BANNER_STYLE[signal.level];
  const narrative = describeAreaSignal(signal, { crimeTrend: report.crime?.trend });
  return (
    <div className={`border rounded-xl p-5 ${style.ring}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${style.dot}`} />
        <span className={`text-sm font-semibold uppercase tracking-wider ${style.text}`}>
          Generally, this area is considered: {style.label}
        </span>
      </div>
      <p className="text-sm text-slate-300 leading-relaxed">{narrative}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-slate-400">
        {signal.decile != null && <span>IMD decile <span className="text-slate-200 font-mono">{signal.decile}/10</span></span>}
        {signal.crimeBand && <span>Crime rate <span className="text-slate-200">{signal.crimeBand}</span></span>}
        {report.crime?.trend && <span>Trend <span className="text-slate-200">{report.crime.trend}</span></span>}
      </div>
    </div>
  );
}

const NeighbourhoodMap = lazy(() => import('../components/maps/NeighbourhoodMap'));

const CRIME_BAND_COLORS = {
  'Low': 'text-emerald-400 bg-emerald-500/15',
  'Below Average': 'text-green-400 bg-green-500/15',
  'Average': 'text-amber-400 bg-amber-500/15',
  'Above Average': 'text-orange-400 bg-orange-500/15',
  'High': 'text-red-400 bg-red-500/15',
};

const TREND_ICONS = {
  'Improving': <TrendingDown className="w-4 h-4 text-emerald-400" />,
  'Stable': <Minus className="w-4 h-4 text-slate-400" />,
  'Worsening': <TrendingUp className="w-4 h-4 text-orange-400" />,
  'Rising Fast': <TrendingUp className="w-4 h-4 text-red-400" />,
};

const PLANNING_ICONS = {
  'conservation-area': <Landmark className="w-4 h-4" />,
  'flood-risk-zone': <Droplets className="w-4 h-4" />,
  'green-belt': <TreePine className="w-4 h-4" />,
  'article-4-direction-area': <ShieldAlert className="w-4 h-4" />,
  'listed-building': <Building2 className="w-4 h-4" />,
  'brownfield-land': <Building2 className="w-4 h-4" />,
  'ancient-woodland': <TreePine className="w-4 h-4" />,
};

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
      <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-blue-400" />}
        {title}
      </h3>
      {children}
    </div>
  );
}

function StatCard({ label, value, sub, color = 'text-white' }) {
  return (
    <div className="bg-slate-900/50 rounded-lg p-3">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value ?? '—'}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Neighbourhood() {
  const { postcode: urlPostcode } = useParams();
  const navigate = useNavigate();
  const [postcode, setPostcode] = useState(urlPostcode || '');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const LEVEL_HINT = {
    area: 'Area overview — use Focus in below to jump to a district',
    district: 'District overview — pick a sector below or search a full unit (e.g. WD25 0HH)',
    sector: 'Sector overview — search a full unit for street-level detail',
    full: 'Full postcode report',
  };

  // Auto-search if postcode in URL
  React.useEffect(() => {
    if (urlPostcode && !report) {
      setPostcode(urlPostcode);
      handleSearchDirect(urlPostcode);
    }
  }, [urlPostcode]);

  React.useEffect(() => {
    const q = postcode.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      fetch(`/api/neighbourhood/suggest?q=${encodeURIComponent(q)}`)
        .then(r => r.ok ? r.json() : { suggestions: [] })
        .then(data => setSuggestions(data.suggestions || []))
        .catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [postcode]);

  const handleSearchDirect = async (pc) => {
    pc = pc.trim().toUpperCase().replace(/\s+/g, ' ').replace(/ +/g, ' ');
    if (pc.length < 2) {
      toast.error('Enter at least 2 characters of a UK postcode (e.g. WD or WD25)');
      return;
    }
    setLoading(true);
    setReport(null);
    setShowSuggestions(false);
    navigate(`/neighbourhood/${encodeURIComponent(pc)}`, { replace: true });
    try {
      const res = await authFetch(`/api/neighbourhood/${encodeURIComponent(pc)}`, {
        timeoutMs: 60000,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = err.detail || 'Area not found';
        throw new Error(
          detail === 'Postcode not found'
            ? 'No data for that search — try a district (WD25), sector (WD25 0), or full postcode (WD25 0HH)'
            : detail
        );
      }
      setReport(await res.json());
    } catch (err) {
      const msg =
        err?.name === 'AbortError'
          ? 'Search timed out — try again (first load can take a moment)'
          : err.message;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    handleSearchDirect(postcode);
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <div>
        <h1 className="text-2xl font-bold text-white">Neighbourhood Report</h1>
        <p className="text-slate-400 text-sm mt-1">
          Start with an area code (WD), district (WD25), or full postcode (WD25 0HH) — then drill down
        </p>
      </div>

      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={postcode}
            onChange={(e) => { setPostcode(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            placeholder="e.g. WD, WD25, WD25 0HH"
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            autoComplete="off"
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul className="absolute z-20 mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg shadow-xl overflow-hidden">
              {suggestions.map((s) => (
                <li key={s.code}>
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-700 flex justify-between gap-2"
                    onMouseDown={() => { setPostcode(s.code); handleSearchDirect(s.code); }}
                  >
                    <span>{s.label || s.code}</span>
                    {s.units > 0 && (
                      <span className="text-slate-500 text-xs">{s.units.toLocaleString()} units</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 font-medium"
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {report && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Header */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <MapPin className="w-5 h-5 text-blue-400" />
              <h2 className="text-xl font-bold text-white">{report.area_label || report.postcode}</h2>
              {report.report_level && report.report_level !== 'full' && (
                <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-300 rounded-full capitalize">
                  {report.report_level} view
                </span>
              )}
              {report.imd_band && (
                <span className="text-xs px-2 py-1 bg-slate-700 rounded-full text-slate-300">
                  IMD: {report.imd_band}
                </span>
              )}
            </div>
            {report.report_level && LEVEL_HINT[report.report_level] && (
              <p className="text-slate-400 text-sm mb-3">{LEVEL_HINT[report.report_level]}</p>
            )}
            {report.drill_down?.length > 0 && (
              <div className="mb-4">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Focus in</div>
                <div className="flex flex-wrap gap-2">
                  {report.drill_down.map((item) => (
                    <button
                      key={item.code}
                      type="button"
                      onClick={() => handleSearchDirect(item.code)}
                      className="text-xs font-medium px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-blue-600 text-slate-200 hover:text-white transition-colors"
                    >
                      {item.label || item.code}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label="Avg Price (1yr)" value={report.avg_price_1yr ? formatCurrency(report.avg_price_1yr) : null} />
              <StatCard label="Avg Price (5yr)" value={report.avg_price_5yr ? formatCurrency(report.avg_price_5yr) : null} />
              <StatCard label="IMD Rank" value={report.imd_rank ? `${report.imd_rank.toLocaleString()} / 32,844` : null} />
              <StatCard label="Classification" value={report.rural_urban || null} />
            </div>
          </div>

          {/* Area summary verdict */}
          <AreaSummaryBanner report={report} />

          {/* Map */}
          {report.latitude && report.longitude && (
            <Suspense fallback={<div className="h-[500px] bg-slate-800/50 rounded-xl animate-pulse" />}>
              <NeighbourhoodMap
                center={{ lat: report.latitude, lng: report.longitude }}
                schools={report.schools || []}
                transport={report.transport || []}
                planning={report.planning || []}
                crimePoints={report.crime_heatmap || []}
                heatmapGated={!!report.gated}
                nearbyProperties={report.nearby_for_sale || []}
                height="500px"
              />
            </Suspense>
          )}

          {/* Broadband */}
          <Section title="Broadband" icon={Wifi}>
            <div className="grid grid-cols-3 gap-3">
              <StatCard
                label="Gigabit Available"
                value={report.broadband_gigabit_pct != null ? `${report.broadband_gigabit_pct}%` : null}
                color={report.broadband_gigabit_pct >= 80 ? 'text-emerald-400' : report.broadband_gigabit_pct >= 30 ? 'text-amber-400' : 'text-red-400'}
              />
              <StatCard
                label="Superfast (30Mb+)"
                value={report.broadband_sfbb_pct != null ? `${report.broadband_sfbb_pct}%` : null}
                color={report.broadband_sfbb_pct >= 90 ? 'text-emerald-400' : 'text-amber-400'}
              />
              <StatCard
                label="Below USO"
                value={report.broadband_below_uso_pct != null ? `${report.broadband_below_uso_pct}%` : null}
                color={report.broadband_below_uso_pct === 0 ? 'text-emerald-400' : 'text-red-400'}
              />
            </div>
          </Section>

          {/* Crime */}
          {report.crime && (
            <Section title="Crime" icon={Shield}>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <StatCard label="Total (12 months)" value={report.crime.total_1yr} />
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 mb-1">Rate</div>
                  <span className={`text-sm font-bold px-2 py-1 rounded ${CRIME_BAND_COLORS[report.crime.rate_band] || 'text-slate-400'}`}>
                    {report.crime.rate_band}
                  </span>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 mb-1">Trend</div>
                  <div className="flex items-center gap-2">
                    {TREND_ICONS[report.crime.trend]}
                    <span className="text-sm font-bold text-white">{report.crime.trend}</span>
                  </div>
                </div>
              </div>
              {report.crime.by_type?.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Breakdown</div>
                  {report.crime.by_type.slice(0, 8).map((t, i) => (
                    <div key={i} className="flex justify-between items-center">
                      <span className="text-sm text-slate-300">{t.type}</span>
                      <span className="text-sm font-mono text-slate-400">{t.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          )}

          {/* Schools */}
          {report.schools?.length > 0 && (
            <Section title="Nearest Schools" icon={GraduationCap}>
              <div className="space-y-2">
                {report.schools.map((s, i) => {
                  const phaseLabel = formatSchoolPhase(s.phase);
                  const faithLabel = formatReligiousCharacter(s.religious_character);
                  return (
                  <div key={i} className="flex items-center justify-between bg-slate-900/50 rounded-lg px-4 py-2.5">
                    <div>
                      <div className="text-sm font-medium text-white">{s.name}</div>
                      <div className="text-xs text-slate-500 flex flex-wrap gap-3 mt-0.5">
                        {phaseLabel && <span>{phaseLabel}</span>}
                        {s.is_selective && <span className="text-amber-400">Selective</span>}
                        {s.is_boarding && <span className="text-purple-400">Boarding</span>}
                        {faithLabel && <span className="text-blue-400">{faithLabel}</span>}
                        {s.number_of_pupils && <span>{s.number_of_pupils} pupils</span>}
                      </div>
                    </div>
                    <span className="text-sm font-mono text-slate-400 whitespace-nowrap">{s.distance_mi} mi</span>
                  </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Transport */}
          {report.transport?.length > 0 && (
            <Section title="Transport" icon={Train}>
              <div className="space-y-2">
                {report.transport.map((t, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-900/50 rounded-lg px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      {t.stop_type === 'BCT' ? <Bus className="w-4 h-4 text-green-400" /> : <Train className="w-4 h-4 text-blue-400" />}
                      <span className="text-sm text-white">{t.name}</span>
                    </div>
                    <span className="text-sm font-mono text-slate-400">{t.distance_mi} mi</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Planning */}
          {report.planning?.length > 0 && (
            <Section title="Planning Constraints" icon={AlertTriangle}>
              {report.planning.some(p => p.dataset === 'flood-risk-zone') && (
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                  Flood labels distinguish <span className="text-slate-300">in zone</span> (postcode
                  point intersects an EA flood polygon) from{' '}
                  <span className="text-slate-300">nearby corridor</span> (canal/river flood area
                  close by — not on this building).
                </p>
              )}
              <div className="space-y-2">
                {report.planning.map((p, i) => {
                  const isFlood = p.dataset === 'flood-risk-zone';
                  const inZone = isFlood && p.relation === 'in_zone';
                  const nearbyFlood = isFlood && p.relation !== 'in_zone';
                  const title = isFlood
                    ? (inZone ? 'In flood zone' : 'Nearby flood corridor')
                    : p.dataset.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  return (
                    <div key={i} className="bg-slate-900/50 rounded-lg px-4 py-2.5">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          {PLANNING_ICONS[p.dataset] || <AlertTriangle className="w-4 h-4" />}
                          <div className="min-w-0">
                            <div className={`text-sm ${inZone ? 'text-red-300' : 'text-white'}`}>
                              {title}
                            </div>
                            {p.name && !isFlood && (
                              <div className="text-xs text-slate-500 truncate">{p.name}</div>
                            )}
                            {p.flood_risk_level && (
                              <span className={`text-xs ${inZone ? 'text-red-400' : 'text-amber-400'}`}>
                                Zone {p.flood_risk_level}
                                {p.flood_risk_type ? ` · ${p.flood_risk_type}` : ''}
                              </span>
                            )}
                            {p.listed_building_grade && (
                              <span className="text-xs text-amber-400">Grade {p.listed_building_grade}</span>
                            )}
                          </div>
                        </div>
                        {p.distance_mi != null && (
                          <span className="text-sm font-mono text-slate-400 shrink-0">
                            {inZone ? 'on point' : `${p.distance_mi} mi`}
                          </span>
                        )}
                      </div>
                      {(nearbyFlood || inZone) && p.note && (
                        <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{p.note}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Sales History */}
          {report.sales_history?.length > 0 && (
            <Section title={report.report_level === 'full' ? 'Recent Sales' : 'Recent Sales in Area'} icon={Home}>
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                Figures are HM Land Registry Price Paid transfers (individual deeds). The same address can
                appear more than once on one completion date when multiple deeds were registered.
              </p>
              <SalesHistoryList sales={report.sales_history} limit={10} />
            </Section>
          )}

          {/* Nearby For Sale */}
          {report.nearby_for_sale?.length > 0 && (
            <Section title="Nearby For Sale" icon={Building2}>
              <div className="space-y-2">
                {report.nearby_for_sale.map((p, i) => (
                  <a key={i} href={`/properties/${p.id}`} className="flex items-center justify-between bg-slate-900/50 rounded-lg px-4 py-2.5 hover:bg-slate-900/80 transition">
                    <div>
                      <div className="text-sm text-white">{p.address}</div>
                      <div className="text-xs text-slate-500">
                        {p.property_type} &middot; {p.bedrooms ? `${p.bedrooms} bed` : ''} &middot; {p.distance_mi} mi away
                      </div>
                    </div>
                    <span className="text-sm font-bold text-blue-400">{formatCurrency(p.asking_price)}</span>
                  </a>
                ))}
              </div>
            </Section>
          )}
        </motion.div>
      )}
    </div>
  );
}
